import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, PartyPopper, Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { StepShell } from "./WizardUI";

const Badge = ({ kind }) => {
  const map = {
    complete: ["Complete", "text-emerald-300 bg-emerald-500/15"],
    connected: ["Connected", "text-emerald-300 bg-emerald-500/15"],
    not_connected: ["Not connected", "text-ink-muted bg-bg-elevated"],
    skipped: ["Skipped", "text-amber-300 bg-amber-500/15"],
    action_required: ["Action required", "text-rose-300 bg-rose-500/15"],
  };
  const [label, cls] = map[kind] || map.not_connected;
  return <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded ${cls}`}>{label}</span>;
};

export default function Step11Review({ state, onboarding, onBack, onEditStep, busy, refresh }) {
  const navigate = useNavigate();
  const [done, setDone] = useState(onboarding?.status === "complete");
  const [completing, setCompleting] = useState(false);
  const [addingStation, setAddingStation] = useState(false);
  const st = state?.station || {};
  const bc = state?.broadcast || {};
  const ss = onboarding?.step_status || {};
  const [social, setSocial] = useState(null);

  useEffect(() => {
    api.get("/social/status").then((r) => {
      setSocial((r.data.platforms || []).filter((p) => p.connected).length);
    }).catch(() => setSocial(0));
  }, []);

  const rows = [
    { label: "Station profile", step: 2,
      status: st.name && (st.primary_format || st.genre) ? "complete" : "action_required",
      detail: st.name ? `${st.name} · ${st.primary_format || st.genre || ""}` : "Name and format missing" },
    { label: "Branding", step: 3,
      status: ss["3"] === "skipped" ? "skipped" : st.logo_url ? "complete" : ss["3"] === "complete" ? "complete" : "not_connected",
      detail: st.logo_url ? "Logo uploaded" : "No logo yet" },
    { label: "Broadcast connection", step: 4,
      status: bc?.connected ? "connected" : ss["4"] === "skipped" ? "skipped" : bc?.provider && bc.provider !== "not_ready" ? "action_required" : "not_connected",
      detail: bc?.provider ? bc.provider.replace(/_/g, " ") : "None selected" },
    { label: "AI DJ", step: 6,
      status: state?.ai_dj ? "complete" : ss["6"] === "skipped" ? "skipped" : "not_connected",
      detail: state?.ai_dj?.name || "Not created" },
    { label: "Social connections", step: 7,
      status: social > 0 ? "connected" : ss["7"] === "skipped" ? "skipped" : "not_connected",
      detail: social != null ? `${social} platform${social === 1 ? "" : "s"} connected` : "…" },
    { label: "Content preferences", step: 5,
      status: st.content_prefs ? "complete" : ss["5"] === "skipped" ? "skipped" : "not_connected",
      detail: st.content_prefs ? `${(st.content_prefs.types || []).length} content types` : "Not set" },
    { label: "API / credit preference", step: 8,
      status: ss["8"] === "complete" ? "complete" : ss["8"] === "skipped" ? "skipped" : "not_connected",
      detail: (onboarding?.steps?.["8"]?.usage_preference || "credits").toUpperCase() },
    { label: "Automations", step: 9,
      status: ss["9"] === "complete" ? "complete" : ss["9"] === "skipped" ? "skipped" : "not_connected",
      detail: st.automation_prefs
        ? `${Object.entries(st.automation_prefs).filter(([k, v]) => v === true).length} enabled`
        : "None enabled" },
  ];

  const complete = async () => {
    setCompleting(true);
    try {
      await api.post("/onboarding/step/11", { data: {}, status: "complete" });
      setDone(true);
      refresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't complete setup"); }
    finally { setCompleting(false); }
  };

  const addStation = async () => {
    setAddingStation(true);
    try {
      await api.post("/onboarding/new-station");
      window.location.href = "/onboarding?step=2";
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't start a new station");
      setAddingStation(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="onboarding-complete">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 mb-6">
          <PartyPopper className="h-8 w-8 text-emerald-300" />
        </div>
        <h1 className="font-display text-3xl font-bold text-ink-primary mb-2">{st.name || "Your station"} is ready</h1>
        <p className="text-sm text-ink-secondary max-w-md mx-auto mb-8">
          Setup complete — your AI-powered broadcast workflow is live. You can revisit any of this from the dashboard checklist.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button type="button" onClick={() => navigate("/dashboard")} data-testid="onboarding-goto-dashboard"
            className="rounded-md bg-brand-amber px-6 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 transition">
            Go to Dashboard
          </button>
          <button type="button" onClick={addStation} disabled={addingStation} data-testid="onboarding-add-station"
            className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-6 py-2.5 text-sm text-ink-secondary hover:text-ink-primary transition disabled:opacity-50">
            {addingStation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add Another Station
          </button>
        </div>
      </div>
    );
  }

  return (
    <StepShell step={11} title="Review & complete"
      subtitle="Here's everything you've set up. Jump back to fix anything, or complete setup now."
      onBack={onBack} onContinue={complete} busy={completing || busy}
      continueLabel="Complete Setup" onSkip={() => navigate("/dashboard")} skipLabel="Save and finish later">
      <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line" data-testid="onboarding-summary">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm text-ink-primary">{r.label}</div>
              <div className="text-[11px] text-ink-muted truncate">{r.detail}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Badge kind={r.status} />
              <button type="button" onClick={() => onEditStep(r.step)} data-testid={`onb-review-edit-${r.step}`}
                className="text-[11px] text-cyan-300 hover:text-cyan-100 transition">Edit</button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 text-[11px] text-ink-muted">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
        Skipped steps can be finished anytime from the dashboard setup checklist.
      </div>
    </StepShell>
  );
}
